import React, { useEffect, useRef, useState } from "react";
import { usePageMeta } from "@/hooks/usePageMeta";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { Sun, Sparkles, BookmarkPlus, Check, Clock, Flame, Share2 } from "lucide-react";
import { api, API } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { TarotCard } from "@/components/TarotCard";
import { ReadingText } from "@/components/ReadingText";
import { ShareCard } from "@/components/ShareCard";
import { generateShareImage } from "@/lib/shareCard";
import { msUntilMidnight, formatCountdown } from "@/lib/daily";

export default function DailyRitual() {
  usePageMeta({ title: "Daily Tarot Card - The Cardlight", description: "Pull one free tarot or oracle card each day with a short AI reflection on it. The Daily Ritual never uses one of your readings.", path: "/daily" });
  const { user, login, loading: authLoading } = useAuth();
  const [decks, setDecks] = useState([]);
  const [phase, setPhase] = useState("loading"); // loading | guest | ready | drawing | revealed
  const [draw, setDraw] = useState(null); // {deck, card:{position,reversed,card}}
  const [flipped, setFlipped] = useState(false);
  const [interpretation, setInterpretation] = useState("");
  const [interpreting, setInterpreting] = useState(false);
  const [saved, setSaved] = useState(false);
  const [countdown, setCountdown] = useState(formatCountdown(msUntilMidnight()));
  const [streak, setStreak] = useState(0);
  const [sharing, setSharing] = useState(false);
  const shareRef = useRef(null);
  const started = useRef(false);
  const generation = useRef(0);

  const handleShare = async () => {
    if (!shareRef.current) return;
    setSharing(true);
    try {
      const res = await generateShareImage(shareRef.current, "the-cardlight-daily-card.png");
      toast.success(res === "shared" ? "Card shared ✦" : "Card image saved ✦");
    } catch (e) {
      toast.error("Could not create the share image.");
    } finally {
      setSharing(false);
    }
  };

  const tzName = () => Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";

  const userKey = user?.user_id || null;
  useEffect(() => {
    // New generation per account: drops results from requests started for a previous user.
    generation.current += 1;
    const gen = generation.current;
    const active = () => gen === generation.current;
    started.current = false;
    setDraw(null);
    setInterpretation("");
    setInterpreting(false);
    setFlipped(false);
    setSaved(false);
    setStreak(0);
    if (authLoading) {
      setPhase("loading");
      return;
    }
    if (!userKey) {
      setPhase("guest");
      return;
    }
    setPhase("loading");
    api.get("/decks").then((res) => { if (active()) setDecks(res.data); }).catch(() => {});
    api
      .get(`/daily?tz=${encodeURIComponent(tzName())}`)
      .then((res) => {
        if (!active()) return;
        const d = res.data;
        setStreak(d.streak || 0);
        if (d.status === "existing") {
          setDraw({ deck: d.deck, card: d.card });
          setInterpretation(d.interpretation || "");
          setFlipped(true);
          setPhase("revealed");
        } else {
          setPhase("ready");
        }
      })
      .catch(() => { if (active()) setPhase("ready"); });
  }, [userKey, authLoading]);

  useEffect(() => {
    const t = setInterval(() => setCountdown(formatCountdown(msUntilMidnight())), 30000);
    return () => clearInterval(t);
  }, []);

  const drawDaily = async () => {
    if (started.current) return;
    started.current = true;
    const gen = generation.current;
    const active = () => gen === generation.current;
    setPhase("drawing");
    setInterpreting(true);
    setInterpretation("");
    let gotCard = false;
    let errored = false;
    try {
      const res = await fetch(`${API}/daily/draw?tz=${encodeURIComponent(tzName())}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
      });
      if (!active()) return;
      if (res.status === 401) {
        toast.info("Please sign in for your daily card.");
        setPhase("guest");
        login();
        return;
      }
      if (!res.body) throw new Error("No stream");
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      while (true) {
        const { done, value } = await reader.read();
        if (done || !active()) break;
        buffer += decoder.decode(value, { stream: true });
        let idx;
        while (active() && (idx = buffer.indexOf("\n\n")) >= 0) {
          const chunk = buffer.slice(0, idx);
          buffer = buffer.slice(idx + 2);
          const line = chunk.split("\n").find((l) => l.startsWith("data:"));
          if (!line) continue;
          try {
            const p = JSON.parse(line.slice(5).trim());
            if (p.card && p.deck && !gotCard) {
              gotCard = true;
              setDraw({ deck: p.deck, card: p.card });
              setPhase("revealed");
              setTimeout(() => setFlipped(true), 250);
            }
            if (p.t) setInterpretation((prev) => prev + p.t);
            if (typeof p.streak === "number") setStreak(p.streak);
            if (p.error) {
              errored = true;
              toast.error(p.error);
            }
          } catch (err) {
            console.debug("Skipping unparseable SSE chunk", err);
          }
        }
      }
      if (errored && active()) {
        started.current = false;
        setPhase("ready");
        setDraw(null);
        setInterpretation("");
      }
    } catch (e) {
      if (!active()) return;
      toast.error("The card resisted today. Try again in a moment.");
      started.current = false;
      setPhase("ready");
    } finally {
      if (active()) setInterpreting(false);
    }
  };

  const saveToJournal = async () => {
    if (!user) {
      toast.info("Sign in to keep your daily card in your journal.");
      login();
      return;
    }
    try {
      await api.post("/readings", {
        deck_id: draw.deck.id,
        spread_id: "daily",
        question: null,
        cards: [{ position: draw.card.position, card_id: draw.card.card.id, reversed: draw.card.reversed }],
        interpretation,
      });
      setSaved(true);
      toast.success("Saved to your journal ✦");
    } catch (e) {
      toast.error("Could not save your daily card.");
    }
  };

  return (
    <div className="ph-no-capture mx-auto max-w-2xl px-5 py-10 sm:px-8 sm:py-14" data-testid="daily-page">
      <motion.div
        initial={{ opacity: 0, y: 18 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="mb-10 text-center"
      >
        <span className="mb-4 inline-flex items-center gap-2 rounded-full border border-[#D9A441]/30 bg-[#131422]/70 px-4 py-1.5 text-xs uppercase tracking-[0.25em] text-[#E5C365]">
          <Sun className="h-3.5 w-3.5" /> daily ritual
        </span>
        <h1 className="font-display text-4xl font-semibold leading-tight tracking-tight text-[#F8F3E6] sm:text-5xl">
          Your Card of the Day
        </h1>
        <p className="mx-auto mt-4 max-w-md text-base leading-relaxed text-[#A3A5B8]">
          One card, drawn from whichever deck calls to you today. Return each day to see what the
          cards wish to show you.
        </p>
        {streak > 0 && (
          <div
            className="mt-6 inline-flex items-center gap-2 rounded-full border border-[#D9A441]/35 bg-[#D9A441]/10 px-4 py-2 text-sm font-medium text-[#E5C365]"
            data-testid="daily-streak"
          >
            <Flame className="h-4 w-4 text-[#D9A441]" />
            {streak}-day ritual streak
          </div>
        )}
      </motion.div>

      <div className="flex flex-col items-center">
        {/* The card */}
        {phase === "loading" ? (
          <div className="flex flex-col items-center gap-4 py-16 text-[#A3A5B8]" data-testid="daily-loading">
            <Sparkles className="h-7 w-7 animate-pulse text-[#D9A441]" />
            <p className="font-display text-lg">Preparing your ritual…</p>
          </div>
        ) : phase === "guest" ? (
          <div
            className="flex w-full max-w-md flex-col items-center gap-6 rounded-2xl border border-[#D9A441]/25 bg-[#131422]/80 p-8 text-center backdrop-blur-xl"
            data-testid="daily-signin-prompt"
          >
            <div className="flex h-20 w-20 items-center justify-center rounded-full border border-[#D9A441]/50">
              <Sun className="h-9 w-9 text-[#D9A441]" strokeWidth={1.3} />
            </div>
            <p className="font-display text-2xl font-semibold text-[#F8F3E6]">Sign in for your daily card</p>
            <p className="text-sm leading-relaxed text-[#A3A5B8]">
              The Daily Ritual is free — signing in simply lets your card and streak follow you across every
              device, and keeps the same card waiting whenever you return today.
            </p>
            <button
              onClick={login}
              data-testid="daily-signin-btn"
              className="rounded-full border border-[#D9A441]/60 bg-[#D9A441] px-8 py-3.5 font-medium text-[#0B0C15] transition-all hover:bg-[#E5C365] hover:shadow-[0_0_28px_rgba(217,164,65,0.4)]"
            >
              Sign in to begin
            </button>
          </div>
        ) : phase === "ready" ? (
          <motion.button
            onClick={drawDaily}
            data-testid="daily-draw-btn"
            whileHover={{ y: -8 }}
            whileTap={{ scale: 0.97 }}
            className="group flex flex-col items-center gap-6"
          >
            <div className="card-3d aspect-[2/3.2] w-48 rounded-xl border border-[#D9A441]/55 bg-[#131422] p-2 shadow-[0_0_28px_rgba(217,164,65,0.18)] sm:w-56">
              <div className="relative flex h-full w-full items-center justify-center overflow-hidden rounded-lg" style={{ border: "1px dashed rgba(217,164,65,0.4)" }}>
                <div className="animate-float-slow flex h-20 w-20 items-center justify-center rounded-full border border-[#D9A441]/50">
                  <Sparkles className="h-8 w-8 text-[#D9A441]" strokeWidth={1.2} />
                </div>
              </div>
            </div>
            <span className="rounded-full border border-[#D9A441]/60 bg-[#D9A441] px-8 py-3.5 font-medium text-[#0B0C15] transition-all group-hover:bg-[#E5C365] group-hover:shadow-[0_0_28px_rgba(217,164,65,0.4)]">
              Draw your daily card
            </span>
            <span className="text-xs italic tracking-wide text-[#A3A5B8]" data-testid="daily-deck-hint">
              The cards will choose your deck ✦
            </span>
          </motion.button>
        ) : phase === "drawing" ? (
          <div className="flex flex-col items-center gap-6" data-testid="daily-drawing">
            <div className="relative h-72 w-48">
              {[0, 1, 2].map((i) => (
                <motion.div
                  key={i}
                  className="absolute inset-0 rounded-xl border border-[#D9A441]/50 bg-[#131422]"
                  animate={{ rotate: [0, i % 2 ? 10 : -10, 0], y: [0, -10, 0] }}
                  transition={{ duration: 1.2, repeat: Infinity, delay: i * 0.12 }}
                >
                  <div className="flex h-full w-full items-center justify-center">
                    <Sparkles className="h-6 w-6 text-[#D9A441]/70" />
                  </div>
                </motion.div>
              ))}
            </div>
            <p className="font-display text-xl text-[#F8F3E6]">Drawing your card…</p>
          </div>
        ) : (
          draw && (
            <TarotCard
              card={draw.card.card}
              reversed={draw.card.reversed}
              flipped={flipped}
              deckType={draw.deck.type}
              testId="daily-card"
            />
          )
        )}

        {/* Reading */}
        <AnimatePresence>
          {phase === "revealed" && (
            <motion.div
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.3 }}
              className="mt-8 w-full max-w-xl rounded-2xl border border-[#D9A441]/25 bg-[#131422]/80 p-6 backdrop-blur-xl sm:p-8"
              data-testid="daily-reading-panel"
            >
              <div className="mb-4 flex items-center gap-3">
                <span className="flex h-8 w-8 items-center justify-center rounded-full border border-[#D9A441]/40 bg-[#0B0C15]/60">
                  <Sparkles className="h-4 w-4 text-[#D9A441]" />
                </span>
                <h2 className="font-display text-xl font-semibold text-[#F8F3E6]">
                  {draw.card.card.title || draw.card.card.name}
                </h2>
              </div>
              <ReadingText text={interpretation} streaming={interpreting} testId="daily-reading-text" />

              {!interpreting && interpretation && (
                <div className="mt-6 flex flex-wrap items-center justify-between gap-3 border-t border-[#D9A441]/15 pt-5">
                  <button
                    onClick={saveToJournal}
                    disabled={saved}
                    data-testid="daily-save-btn"
                    className="flex items-center gap-2 rounded-full border border-[#D9A441]/50 bg-[#D9A441]/10 px-5 py-2.5 text-sm font-medium text-[#E5C365] transition-all hover:bg-[#D9A441]/20 disabled:opacity-60"
                  >
                    {saved ? <Check className="h-4 w-4" /> : <BookmarkPlus className="h-4 w-4" />}
                    {saved ? "Saved to journal" : user ? "Save to journal" : "Sign in to save"}
                  </button>
                  <div className="flex items-center gap-2 text-sm text-[#A3A5B8]" data-testid="daily-countdown">
                    <Clock className="h-4 w-4 text-[#D9A441]" />
                    Next card in {countdown}
                  </div>
                </div>
              )}
              {!interpreting && interpretation && (
                <div className="mt-3 flex justify-end">
                  <button
                    onClick={handleShare}
                    disabled={sharing}
                    data-testid="daily-share-btn"
                    className="flex items-center gap-2 rounded-full border border-[#D9A441]/30 px-5 py-2.5 text-sm text-[#E5C365] transition-colors hover:border-[#D9A441]/60 hover:bg-[#D9A441]/10 disabled:opacity-60"
                  >
                    <Share2 className="h-4 w-4" /> {sharing ? "Creating…" : "Share this card"}
                  </button>
                </div>
              )}
              {draw && (
                <ShareCard
                  ref={shareRef}
                  deckName={draw.deck?.name}
                  spreadName={draw.deck?.type === "oracle" ? "Oracle" : "Daily Ritual"}
                  cards={[draw.card]}
                  interpretation={interpretation}
                />
              )}
            </motion.div>
          )}
        </AnimatePresence>

        {/* Gentle reminder when already drawn today */}
        {phase === "revealed" && !interpreting && (
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.6 }}
            className="mt-6 text-center text-sm italic text-[#A3A5B8]"
            data-testid="daily-return-note"
          >
            You've received today's guidance. Return tomorrow for a new card ✦
          </motion.p>
        )}
      </div>
    </div>
  );
}
